// RECALL-ADMIN-MEMORY twin (services/api/src/domain/memory/admin.ts): Setup › Memory for owners
// and admins. The memory service's `admin` act is asked of Recall and not live yet, so the twin
// answers as the server does meanwhile: `service_ready: false`, records from what agents told
// memory (kept here), flag / archive / restore working (the install doors are live), everything
// that needs the admin act answering `waiting` and changing nothing, and batch settle / clearing
// injection flags filed as operator jobs. A company without Recall has told memory nothing: the
// spaces are listed, empty. The owner's "See all" switch is the lane's (one switch, one state);
// without it the other teams' agents' spaces are not listed, and personal spaces never are.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, type Principal, problem, validation } from "./fake";
import type { FakeCompany } from "./fake-data";
import { uid } from "./fake-data";

type Browse = OperationOutput<"memory.adminBrowse">;
type Space = Browse["spaces"][number];
type Rec = Browse["records"][number];
type RecState = Rec["state"];
type Filed = OperationOutput<"memory.adminJobFile">;
type Job = NonNullable<Filed["job"]>;
type Acted = OperationOutput<"memory.adminRecordAct">;

const WAITING = "Waiting on memory service";

export const FAKE_MEMORY_ADMIN_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>([
  "memory.adminOverview",
  "memory.adminBrowse",
  "memory.adminRecord",
  "memory.adminRecordAct",
  "memory.adminSpaces",
  "memory.adminCharter",
  "memory.adminAgentAccess",
  "memory.adminRules",
  "memory.adminRuleAct",
  "memory.adminHealth",
  "memory.adminJobs",
  "memory.adminJobFile",
]);

type Local = Rec & { full: string; evidence: string; sent: boolean };
type State = { records: Local[]; jobs: Job[]; seq: number };

export function createMemoryAdminFake(deps: {
  now: () => Date;
  /** Whether the twin's Recall is connected (otherwise memory was told nothing). */
  live: boolean;
  /** The Memory lane's switch for this company, and the lane's open cards. */
  seeAll: (c: FakeCompany) => boolean;
  laneOpen: (c: FakeCompany) => number;
}): Partial<Record<RouteId, Handler>> {
  const states = new Map<string, State>();
  const at = (minutesAgo: number) =>
    new Date(deps.now().getTime() - minutesAgo * 60_000).toISOString();

  const seatKey = (n: number) => `seat:${uid(0x2000, 0xb00 + n)}`;
  const RECRUITER = { key: seatKey(1), id: uid(0x1000, 0xb01), name: "Recruiter" };
  const FINANCE = { key: seatKey(2), id: uid(0x1000, 0xb02), name: "Finance clerk" };

  function stateOf(c: FakeCompany): State {
    let s = states.get(c.id);
    if (s) return s;
    s = { records: [], jobs: [], seq: 0 };
    states.set(c.id, s);
    if (!deps.live) return s;
    const rec = (
      i: number,
      space: { key: string; name: string },
      type: string,
      title: string,
      full: string,
      by: string,
      minutesAgo: number,
      state: RecState = "live",
    ): Local => ({
      key: `w:${uid(0x7c00, i)}`,
      space_key: space.key,
      space_name: space.name,
      type,
      title,
      text: full.length > 120 ? `${full.slice(0, 117)}...` : full,
      by,
      at: at(minutesAgo),
      state,
      source: "companyos",
      full,
      evidence: `task ${uid(0x7d00, i)}, run ${uid(0x7e00, i)}`,
      sent: state !== "queued" && state !== "proposed",
    });
    s.records = [
      rec(
        1,
        RECRUITER,
        "Pattern",
        "Candidates who wait over two days for a reply drop out three times as often",
        "Candidates who wait over two days for a reply drop out three times as often. Seen across 41 screening threads this quarter.",
        "Recruiter DE",
        60 * 5,
      ),
      rec(
        2,
        RECRUITER,
        "Decision",
        "Send CVs to clients only with the candidate's written consent",
        "Send CVs to clients only with the candidate's written consent, and keep the consent note on the candidate's record.",
        "Recruiter DE",
        60 * 26,
      ),
      rec(
        3,
        RECRUITER,
        "Note",
        "Northwind wants CVs as Word files",
        "Northwind wants CVs as Word files, not PDFs. Their tracker strips PDF formatting.",
        "Recruiter DE",
        60 * 48,
      ),
      rec(
        4,
        RECRUITER,
        "Note",
        "Screening call notes go in the candidate page",
        "Screening call notes go in the candidate page, under Calls.",
        "Recruiter DE",
        60 * 70,
        "flagged",
      ),
      rec(
        5,
        RECRUITER,
        "Pattern",
        "Friday afternoon sends get the fewest replies",
        "Friday afternoon sends get the fewest replies. Hold them to Monday morning.",
        "Recruiter DE",
        20,
        "queued",
      ),
      rec(
        6,
        FINANCE,
        "Decision",
        "Client invoices go out on the 25th",
        "Client invoices go out on the 25th of each month, unless the 25th is a weekend, then the Friday before.",
        "Finance clerk DE",
        60 * 30,
      ),
      rec(
        7,
        FINANCE,
        "Fact",
        "Payroll runs through the bank's batch file",
        "Payroll runs through the bank's batch file; the upload closes at 14:00.",
        "Finance clerk DE",
        60 * 90,
      ),
    ];
    return s;
  }

  const member = (p: Principal | null): p is Principal =>
    p?.roles.some((r) => r !== "client_viewer") === true;
  const denied = (p: Principal | null): Response | null =>
    !p
      ? notFound()
      : !member(p) || !p.manager
        ? problem(403, "forbidden", "Forbidden", "Managers only")
        : null;

  /** The hourly watch's latest numbers (kept short: a preview needs something to read). */
  function qualityOf(spaces: Space[], live: boolean) {
    const numbers = (writes: number, dup: number | null) => ({
      writes,
      duplicate_share: dup,
      typed_writes: writes,
      excerpt_events: null,
      excerpt_share: null,
      why_share: null,
      contradictions_open: live ? 2 : 0,
      oldest_contradiction_days: live ? 6 : null,
      stale_proposals: live ? 1 : 0,
      brief_chars: live ? 4200 : null,
      reads_per_run: live ? 6.5 : null,
      stale_facts: live ? 2 : 0,
      flood: false,
    });
    return {
      as_of: at(20),
      company: numbers(live ? 14 : 3, live ? 0.14 : 0),
      spaces: spaces.map((x, i) => ({
        key: x.key,
        name: x.name,
        writes: i === 0 ? 3 : 11,
        duplicate_share: i === 0 ? 0 : 0.18,
        stale_proposals: live ? 1 : 0,
        contradictions_open: live ? (i === 0 ? 2 : 0) : 0,
      })),
      writers: live
        ? [
            {
              id: RECRUITER.id,
              name: RECRUITER.name,
              writes: 11,
              duplicate_share: 0.18,
              flood: false,
            },
          ]
        : [],
      facts: {
        stale_records: live ? 2 : 0,
        suggestions_open: live ? 1 : 0,
        checked_at: at(35),
      },
      waiting: [
        "excerpts",
        "why",
        ...(live ? [] : (["brief", "reads"] as const)),
        "member_reads",
      ] as ("excerpts" | "why" | "brief" | "reads" | "member_reads")[],
    };
  }

  /** The spaces the caller is shown: shared ones, their own agent, and others with the switch. */
  function spacesOf(p: Principal, s: State): Space[] {
    const all = p.roles.includes("owner") && deps.seeAll(p.company);
    const counts = (key: string) => s.records.filter((r) => r.space_key === key);
    const space = (
      key: string,
      name: string,
      kind: Space["kind"],
      why: Space["why_here"],
      agent: Space["agent"] = null,
    ): Space => {
      const rs = counts(key);
      const by: Record<string, number> = {};
      for (const r of rs) by[r.type] = (by[r.type] ?? 0) + 1;
      return {
        key,
        name,
        kind,
        agent,
        owners: agent ? [] : [],
        records: rs.length,
        counted: kind === "agent",
        by_type: by,
        why_here: why,
      };
    };
    const out: Space[] = [space("company", p.company.name, "company", "shared")];
    if (deps.live) {
      out.push(
        space(RECRUITER.key, RECRUITER.name, "agent", "yours", {
          id: RECRUITER.id,
          name: RECRUITER.name,
          label: "DE",
        }),
      );
      if (all)
        out.push(
          space(FINANCE.key, FINANCE.name, "agent", "owner_all", {
            id: FINANCE.id,
            name: FINANCE.name,
            label: "DE",
          }),
        );
    }
    return out;
  }

  const seeAllOf = (p: Principal) => ({
    available: p.roles.includes("owner"),
    on: p.roles.includes("owner") && deps.seeAll(p.company),
  });
  const recordOf = (r: Local): Rec => ({
    key: r.key,
    space_key: r.space_key,
    space_name: r.space_name,
    type: r.type,
    title: r.title,
    text: r.text,
    by: r.by,
    at: r.at,
    state: r.state,
    source: r.source,
  });
  const reach = (p: Principal, s: State, r: Local) =>
    spacesOf(p, s).some((x) => x.key === r.space_key);
  const waiting = (): Acted => ({ result: "waiting", note: WAITING, state: null });

  return {
    "memory.adminOverview": ({ principal: p }) => {
      const no = denied(p);
      if (no) return no;
      const s = stateOf((p as Principal).company);
      return json({
        service_ready: false,
        waiting_note: WAITING,
        see_all: seeAllOf(p as Principal),
        review_open: deps.laneOpen((p as Principal).company),
        jobs_open: s.jobs.filter((j) => j.state === "open").length,
      } satisfies OperationOutput<"memory.adminOverview">);
    },
    "memory.adminBrowse": ({ principal: p, url }) => {
      const no = denied(p);
      if (no) return no;
      const pr = p as Principal;
      const s = stateOf(pr.company);
      const spaces = spacesOf(pr, s);
      const asked = url.searchParams.get("space");
      const chosen = asked ? spaces.find((x) => x.key === asked) : undefined;
      const type = url.searchParams.get("type");
      const q = (url.searchParams.get("q") ?? "").toLowerCase();
      const records =
        asked && !chosen
          ? []
          : s.records
              .filter((r) => (chosen ? r.space_key === chosen.key : reach(pr, s, r)))
              .filter((r) => (type ? r.type === type : true))
              .filter((r) => (q ? `${r.title} ${r.full}`.toLowerCase().includes(q) : true))
              .map(recordOf);
      return json({
        service_ready: false,
        waiting_note: WAITING,
        see_all: seeAllOf(pr),
        source: "companyos",
        spaces,
        space: chosen?.key ?? null,
        topics: [],
        records,
        next_cursor: null,
        unavailable: null,
      } satisfies Browse);
    },
    "memory.adminRecord": ({ principal: p, url }) => {
      const no = denied(p);
      if (no) return no;
      const pr = p as Principal;
      const s = stateOf(pr.company);
      const r = s.records.find((x) => x.key === url.searchParams.get("key"));
      if (!r || !reach(pr, s, r)) return notFound();
      return json({
        record: recordOf(r),
        full_text: r.full,
        reason: null,
        evidence: r.evidence,
        versions: [{ n: 1, at: r.at, by: r.by, text: r.full, reason: null }],
        trace: [
          { at: r.at, kind: "saved", by: r.by, note: `${r.type}, queued` },
          ...(r.sent
            ? [{ at: r.at, kind: "sent", by: null, note: "told to the memory service" }]
            : []),
        ],
        expand: [],
        chains: [],
        timeline: [],
        service_ready: false,
        waiting_note: `${WAITING}: full history, links and chains`,
        can_act: r.sent,
      } satisfies OperationOutput<"memory.adminRecord">);
    },
    "memory.adminRecordAct": ({ principal: p, body }) => {
      const no = denied(p);
      if (no) return no;
      const pr = p as Principal;
      const s = stateOf(pr.company);
      const b = (body ?? {}) as { key?: unknown; action?: unknown };
      if (typeof b.key !== "string" || !["flag", "archive", "restore"].includes(String(b.action)))
        return validation("key and action: flag, archive or restore");
      const r = s.records.find((x) => x.key === b.key);
      if (!r || !reach(pr, s, r)) return notFound();
      if (!r.sent)
        return json({
          result: "waiting",
          note: "Not in memory yet: it is still waiting to be saved",
          state: r.state,
        } satisfies Acted);
      if (b.action === "restore" && r.state !== "archived")
        return json({
          result: "refused",
          note: "Only an archived record can be restored",
          state: r.state,
        } satisfies Acted);
      if (b.action !== "restore" && r.state === "archived")
        return json({
          result: "refused",
          note: "It is archived already",
          state: r.state,
        } satisfies Acted);
      r.state = b.action === "flag" ? "flagged" : b.action === "archive" ? "archived" : "live";
      return json({ result: "done", note: null, state: r.state } satisfies Acted);
    },
    "memory.adminSpaces": ({ principal: p }) => {
      const no = denied(p);
      if (no) return no;
      const pr = p as Principal;
      const s = stateOf(pr.company);
      return json({
        service_ready: false,
        waiting_note: WAITING,
        spaces: spacesOf(pr, s).map((x) => ({
          ...x,
          charter: null,
          agent_access: null,
          who_knows: x.agent
            ? [{ name: x.agent.name, kind: "agent" as const, topics: [], writes: x.records }]
            : [],
          knows_source: "companyos" as const,
          can_tend: false,
        })),
      } satisfies OperationOutput<"memory.adminSpaces">);
    },
    "memory.adminCharter": ({ principal: p, params, body }) => {
      const no = denied(p);
      if (no) return no;
      const pr = p as Principal;
      if (
        !spacesOf(pr, stateOf(pr.company)).some(
          (x) => x.key === decodeURIComponent(params.space ?? ""),
        )
      )
        return notFound();
      const text = (body as { text?: unknown } | null)?.text;
      if (typeof text !== "string" || !text.trim()) return validation("text is required");
      return json(waiting());
    },
    "memory.adminAgentAccess": ({ principal: p, params, body }) => {
      const no = denied(p);
      if (no) return no;
      const pr = p as Principal;
      if (
        !spacesOf(pr, stateOf(pr.company)).some(
          (x) => x.key === decodeURIComponent(params.space ?? ""),
        )
      )
        return notFound();
      if (
        !["none", "recall", "record"].includes(
          String((body as { agent_access?: unknown } | null)?.agent_access),
        )
      )
        return validation("agent_access: none, recall or record");
      return json(waiting());
    },
    "memory.adminRules": ({ principal: p }) => {
      const no = denied(p);
      if (no) return no;
      return json({
        service_ready: false,
        waiting_note: WAITING,
        spaces: [],
        unavailable: [],
        truncated: false,
      } satisfies OperationOutput<"memory.adminRules">);
    },
    "memory.adminRuleAct": ({ principal: p }) => {
      const no = denied(p);
      if (no) return no;
      return json(waiting());
    },
    "memory.adminHealth": ({ principal: p }) => {
      const no = denied(p);
      if (no) return no;
      const pr = p as Principal;
      const s = stateOf(pr.company);
      const mine = s.records.filter((r) => reach(pr, s, r));
      return json({
        service_ready: false,
        waiting_note: WAITING,
        writes: {
          sent: mine.filter((r) => r.sent).length,
          queued: mine.filter((r) => r.state === "queued").length,
          proposed: 0,
          rejected: 0,
        },
        outbox: {
          pending: mine.filter((r) => r.state === "queued").length,
          held: 0,
          by_reason: [],
        },
        lane_open: deps.laneOpen(pr.company),
        answers_waiting: 0,
        contradictions_open: deps.live ? 2 : 0,
        spaces: spacesOf(pr, s).map((x) => ({
          key: x.key,
          name: x.name,
          records: x.records,
          stale: null,
          duplicates: null,
          never_recalled: null,
          injections: null,
        })),
        burden: null,
        inert_grants: [],
        injection_flags: [],
        jobs: s.jobs,
        quality: qualityOf(spacesOf(pr, s), deps.live),
      } satisfies OperationOutput<"memory.adminHealth">);
    },
    "memory.adminJobs": ({ principal: p }) => {
      const no = denied(p);
      if (no) return no;
      return json({ jobs: stateOf((p as Principal).company).jobs });
    },
    "memory.adminJobFile": ({ principal: p, body }) => {
      const no = denied(p);
      if (no) return no;
      const pr = p as Principal;
      const s = stateOf(pr.company);
      const b = (body ?? {}) as { kind?: unknown; space?: unknown; mode?: unknown };
      if (b.kind !== "batch_settle" && b.kind !== "clear_injection_flags")
        return validation("kind: batch_settle or clear_injection_flags");
      if (typeof b.space !== "string") return validation("space is required");
      const filed = (job: Job | null, result: Filed["result"], note: string | null) =>
        json({ result, note, job } satisfies Filed);
      // a space is one this caller can see (or "all", for settling)
      const spaces = spacesOf(pr, s);
      if (b.space !== "all" && !spaces.some((x) => x.key === b.space)) return notFound();
      if (b.kind === "clear_injection_flags")
        return b.space === "all"
          ? validation("name one space to clear flags in")
          : filed(null, "waiting", WAITING);
      if (!deps.live) return filed(null, "nothing", "There is nothing waiting to settle there");
      const mode = b.mode === "dismiss" ? "dismiss" : "keep_newer";
      const open = s.jobs.find(
        (j) =>
          j.kind === "batch_settle" &&
          j.state === "open" &&
          j.space_key === b.space &&
          j.mode === mode,
      );
      if (open) return filed(open, "filed", null);
      const job: Job = {
        id: uid(0x7f00, ++s.seq),
        kind: "batch_settle",
        space_key: b.space,
        space_name:
          b.space === "all"
            ? "All spaces you see"
            : (spaces.find((x) => x.key === b.space)?.name ?? null),
        mode,
        pairs: 2,
        state: "open",
        filed_by: "You",
        filed_at: at(0),
        closed_at: null,
        note: null,
      };
      s.jobs = [job, ...s.jobs];
      return filed(job, "filed", null);
    },
  };
}
